import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useAuth } from '../auth/AuthContext';
import { apiGet } from '../api/http';
import PageHeader from '../components/PageHeader';

function BigStat({ label, value, loading }) {
  if (loading) {
    return (
      <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 92 }}>
        <Skeleton variant="text" width={64} height={44} />
        <Skeleton variant="text" width={72} height={18} />
      </Stack>
    );
  }

  if (typeof value !== 'number') return null;

  return (
    <Stack alignItems="flex-end" spacing={0} sx={{ minWidth: 92 }}>
      <Typography
        variant="h3"
        sx={{
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: -0.5,
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function DashboardCard({ title, subtitle, icon, onOpen, statLabel, statValue, statLoading }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea
        onClick={onOpen}
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'stretch',
          '&:hover .lp-card': { transform: 'translateY(-2px)' },
        }}
      >
        <CardContent
          className="lp-card"
          sx={{
            p: 3,
            width: '100%',
            transition: 'transform 180ms ease',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar
              variant="rounded"
              sx={{
                width: 46,
                height: 46,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                flex: '0 0 auto',
              }}
            >
              {icon}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ mb: 0.25 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {subtitle}
                  </Typography>
                </Box>

                <BigStat label={statLabel} value={statValue} loading={statLoading} />
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
                <ChevronRightRoundedIcon sx={{ opacity: 0.75 }} />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function formatTime(dt) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dt);
  } catch {
    return dt.toLocaleString();
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [activeCount, setActiveCount] = React.useState(null);
  const [oldCount, setOldCount] = React.useState(null);
  const [countsLoading, setCountsLoading] = React.useState(true);
  const [lastRefreshed, setLastRefreshed] = React.useState(null);

  const loadCounts = React.useCallback(async () => {
    setCountsLoading(true);
    try {
      const data = await apiGet('/api/projects');

      if (Array.isArray(data)) {
        const active = data.filter((p) => (p.status ?? '').toLowerCase() === 'active').length;
        const old = data.filter((p) => (p.status ?? '').toLowerCase() !== 'active').length;
        setActiveCount(active);
        setOldCount(old);
      } else {
        setActiveCount(0);
        setOldCount(0);
      }
      setLastRefreshed(new Date());
    } catch {
      setActiveCount(null);
      setOldCount(null);
    } finally {
      setCountsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await loadCounts();
    })();
    return () => {
      alive = false;
    };
  }, [loadCounts]);

  const subtitle = `Quick access to your daily actions.${
    lastRefreshed ? `  •  Last refreshed: ${formatTime(lastRefreshed)}` : ''
  }`;

  // NOTE:
  // 64px = typical top AppBar height
  // 32px = py:4 on outer Box (16px top + 16px bottom)
  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', py: 4 }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          px: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 64px - 32px)',
        }}
      >
        {/* Header stays at top */}
        <PageHeader
          title="Dashboard"
          subtitle={subtitle}
          actions={
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={loadCounts}
              disabled={countsLoading}
            >
              Refresh
            </Button>
          }
        />

        {/* Cards are centered within the remaining space */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Grid container spacing={2.2} alignItems="stretch">
            {isAdmin && (
              <Grid item xs={12} sm={6} md={3}>
                <DashboardCard
                  title="Create New Project"
                  subtitle="Start a new loan case and capture all required details."
                  icon={<AddCircleOutlineIcon />}
                  onOpen={() => navigate('/project/new')}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <DashboardCard
                title="Active Projects"
                subtitle="View and continue work on ongoing cases."
                icon={<ViewKanbanOutlinedIcon />}
                statLabel="ACTIVE"
                statValue={activeCount}
                statLoading={countsLoading}
                onOpen={() => navigate('/projects?showCompleted=0')}
              />
            </Grid>

            {isAdmin && (
              <Grid item xs={12} sm={6} md={3}>
                <DashboardCard
                  title="Completed Projects"
                  subtitle="View completed/closed projects."
                  icon={<ArchiveOutlinedIcon />}
                  statLabel="COMPLETED"
                  statValue={oldCount}
                  statLoading={countsLoading}
                  onOpen={() => navigate('/projects?showCompleted=1')}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <DashboardCard
                title="Loan Eligibility"
                subtitle="Run quick checks and compare options."
                icon={<CalculateOutlinedIcon />}
                onOpen={() => navigate('/loan-eligibility')}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
